import { RFResponse, RFResponseCommon, RFUpdateResponse } from "@/interfaces/common";
import { BridgeCallHandlers, recaptchaScene } from "@/interfaces/enums";
import {
	BindPhoneOrEmailRequest,
	LoginParams,
	RegionModel,
	RegisterParams,
	VerificationCodeWithoutRiskRequest,
	VerifyCodeRequest,
	VerifyCodeResponse,
} from "@/interfaces/loginTypes";
import bridge from "@/utils/dsBridge";
import log2sensors, { SensorsEventIds, SensorsVerifyCodeScene } from "@/utils/log2sensors";
import log2Sentry from "@/utils/log2Sentry";
import * as http from "@/utils/request";
import useSWR from "swr";
import rfStore from "@/utils/rfStore";

const baseUrl = process.env.NEXT_PUBLIC_BASE_API;

// 获取地域列表
export const getRegions = async (lang: string): Promise<RegionModel[]> => {
	const url = baseUrl + `/geolocation/api/countries?smsAvailable=true&sticky=false&language=${lang}`;

	try {
		const { code, message, data } = await http.get<RFResponseCommon<RegionModel[]>>(url);

		if (code !== 200) {
			throw Error(message);
		}

		return data;
	} catch (error) {
		console.log(error);
		return [];
	}
};

// 获取验证码
export const sendVerifiedCode = async (
	type: number,
	identity: string,
	securityToken?: string,
	scene = SensorsVerifyCodeScene.login,
): Promise<void> => {
	const url = baseUrl + "/verification/api/captcha/app/security/anonymous";

	try {
		log2sensors(SensorsEventIds.start_request_code, {
			countryCode: type == 1 ? identity.replaceAll(" ", "").substring(0, 4) : "0000",
			account: identity,
			scene,
		});

		const { code, message } = await http.post<
			{
				type: number;
				identity: string;
				securityToken?: string;
				captchaChannel?: string;
			},
			RFResponseCommon<any>
		>(
			url,
			{
				type: type,
				identity,
				securityToken,
				captchaChannel: "ALI_CLOUD",
			},
			{
				headers: {
					platformType: "3", // WARNING: 这里有坑，通过这个接口 只能传 3
				},
			},
		);
		if (code !== 200) {
			throw Error(message);
		}

		log2sensors(SensorsEventIds.response_request_code, {
			countryCode: type === 1 ? identity.substring(0, 4) : "0000",
			account: identity,
			isSucc: 0,
			code: 200,
			fail_reason: "",
			scene,
		});
	} catch (error) {
		let message = "";
		if (error instanceof Error) {
			message = error.message;
		}

		log2sensors(SensorsEventIds.response_request_code, {
			countryCode: type === 1 ? identity.substring(0, 4) : "0000",
			account: identity,
			isSucc: 1,
			code: 400,
			fail_reason: message,
			scene,
		});

		throw error;
	}
};

export const login = async (params: LoginParams) => {
	const url = baseUrl + "/account/api/token";
	try {
		const { code, message, data } = await http.post<
			LoginParams,
			RFResponseCommon<{
				token: string;
				userId: string;
			}>
		>(url, params);
		if (code !== 200) {
			throw Error(message);
		}
		return data;
	} catch (error) {
		console.log(error);
	}
};

// 判断是否登录
export const hasRegister = async (identity: string, type: number) => {
	const url = `${baseUrl}/account/api/users/accounts?type=${type}&identity=${identity}`;
	try {
		const { code, message, data } = await http.get<RFResponseCommon<{ existent: boolean }>>(url);
		if (code !== 200) {
			throw Error(message);
		}
		return data.existent;
	} catch (error) {
		console.log(error);
	}
};

// 注册
export const register = async (params: RegisterParams) => {
	const url = baseUrl + "/account/api/users/accounts";
	try {
		const { code, message, data } = await http.post<
			RegisterParams,
			RFResponseCommon<{
				token: string;
				userId: string;
			}>
		>(url, params);
		if (code !== 200) {
			throw Error(message);
		}
		return data;
	} catch (error) {
		console.log(error);
	}
};

/**
 * 短信/邮件验证码发送 (不带风控)
 */
export const sendVerifiedCodeWithoutRisk = async (params: VerificationCodeWithoutRiskRequest): Promise<void> => {
	const url = baseUrl + "/verification/api/captcha/app/security/anonymous";

	try {
		const { code } = await http.post<VerificationCodeWithoutRiskRequest, RFUpdateResponse>(url, params, {
			headers: {
				platformType: "3", // WARNING: 这里有坑，通过这个接口 只能传 3
			},
		});
	} catch (error) {
		throw error;
	}
};
/**
 * 短信/邮件验证码校验
 */
export const validateCodeIsSafety = async (
	type: number,
	identity: string,
	captcha: string,
	scene = SensorsVerifyCodeScene.login,
) => {
	const url = baseUrl + "/verification/api/captcha";

	try {
		log2sensors(SensorsEventIds.start_request_verify_code, {
			countryCode: type === 1 ? identity.substring(0, 4) : "0000",
			account: identity,
			verifyCode: captcha,
			isH5: true,
		});

		const { code, data } = await http.patch<VerifyCodeRequest, RFResponse<VerifyCodeResponse>>(url, {
			type,
			identity,
			captcha,
		});
		if (code !== 200) {
			throw Error(`verification error: ${code}`);
		}
		log2sensors(SensorsEventIds.response_verify_code, {
			countryCode: type === 1 ? identity.substring(0, 4) : "0000",
			account: identity,
			verifyCode: captcha,
			code: 200,
			fail_reason: "",
			scene,
			isH5: true,
		});

		log2sensors(SensorsEventIds.login_discernCode, {
			login_type: type === 1 ? "手机号" : "邮箱",
			account: identity,
			verifyCode: captcha,
			is_success: true,
		});

		return data;
	} catch (error: any) {
		const msg = error instanceof Error ? error.message : "safe error";
		log2sensors(SensorsEventIds.response_verify_code, {
			countryCode: type === 1 ? identity.substring(0, 4) : "0000",
			account: identity,
			verifyCode: captcha,
			code: 400,
			fail_reason: msg,
			scene,
			isH5: true,
		});
		log2sensors(SensorsEventIds.login_discernCode, {
			login_type: type === 1 ? "手机号" : "邮箱",
			account: identity,
			verifyCode: captcha,
			is_success: false,
			fail_reason: 400,
			fail_reason_des: msg,
		});
		log2Sentry(error, "error");
		throw error;
	}
};
/**
 * 聚合接口绑定手机号/邮箱
 */
export const bindPhoneOrEmail = async (payload: BindPhoneOrEmailRequest) => {
	const url = baseUrl + "/_api/masterAccounts/identity/bind";
	await http.post<BindPhoneOrEmailRequest, RFUpdateResponse>(url, payload);
};

/**
 * 从客户端获取 recaptcha token
 */

export const getRecaptchaToken = () => {
	const promise = new Promise((resolve, reject) => {
		bridge.call(
			BridgeCallHandlers.ON_GET_RECAPTCHA_TOKEN,
			{
				sceneName: recaptchaScene.bind_email_or_phone,
			},
			(res: string) => {
				try {
					const data = JSON.parse(res);
					const token = data.token;
					if (token === "un_available_token") {
						throw Error(token);
					}

					resolve(token);
				} catch (error) {
					reject(error);
				}
			},
		);
	});

	return promise;
};

// 获取渠道
export const useChannelSources = () => {
	const url = `${baseUrl}/configuration/api/sources`;

	const { data, isLoading, mutate } = useSWR(
		url,
		async () =>
			await http.get<RFResponseCommon<{ sourceId: string; sourceName: string }[]>>(url).then((res) => res.data),
	);

	return {
		data,
		isLoading,
		mutate,
	};
};

/**
 * @summary 检查是否为GUGU账户
 * @param type 账户类型 1 手机 2 邮箱
 * @param identity 账户
 * @returns 是否为GUGU账户
 */

export const checkIsGuguAccount = async (type: number, identity: string) => {
	const url = `${baseUrl}/masterAccount/api/judge/isPartnerUser?type=${type}&identity=${identity}`;
	try {
		const { code, message, data } = await http.get<RFResponseCommon<{ partner: string }>>(url);
		if (code !== 200) {
			throw Error(message);
		}
		return !!(data.partner && data.partner.startsWith("gugu"));
	} catch (error) {
		console.log(error);
		return false;
	}
};

/**
 * @summary 检查是否需要google验证
 * @param captcha_type GOOGLE, ALI_CLOUD
 * @param type 1 手机 2 邮箱
 * @param identity 账号
 */
export const checkNeedGoogleValidate = async (
	captcha_type = "ALI_CLOUD",
	type: number,
	identity: string,
) => {
	const url = `${baseUrl}/verification/api/captcha/app/security/anonymous/doRisk?captcha_type=${captcha_type}&type=${type}&identity=${encodeURIComponent(identity)}`;
	try {
		const { code, message, data } = await http.get<RFResponseCommon<boolean>>(url);
		if (code !== 200) {
			throw Error(message);
		}
		return data;
	} catch (error) {
		throw error;
	}
};

/**
 * @summary 跳过绑定手机号
 */
export const skipBindPhone = async () => {
	try {
		const uid = rfStore.getRFState()?.userId;
		const url = `${baseUrl}/masterAccount/api/users/${uid}/identity/skip`;
		const { code, message } = await http.patch<any, RFResponseCommon<null>>(url, {});
		if (code !== 200 && code !== 201) throw Error(message);
		return true;
	} catch (error) {
		throw error;
	}
};
